import React from 'react'
import { useGlobalState } from 'state'
import { Coordinates } from 'common/types'
import { splitImageWithCoordinates } from 'common/utils'

type SplitImageLayerProps = {
  url: string | null
  coordinates: Coordinates
  sourceId: string
  layerId: string
  splitNum?: number
  opacity?: number
  visibility?: boolean
}

export const SplitImageLayer = ({url, coordinates, sourceId, layerId, splitNum = 10, opacity = 0.8, visibility = true}: SplitImageLayerProps) => {
  const state = useGlobalState()

  React.useEffect(() => {
    return () => {
      remove()
    }
  }, [])

  React.useEffect(() => {
    if (url && coordinates.length === 4) {
      load()
    }
  }, [url, coordinates])

  React.useEffect(() => {
    if (!state.mapbox) return
    for (let i = 0; i < splitNum; i++) {
      const id = `${layerId}-${i}`
      if (!state.mapbox.getLayer(id)) continue
      state.mapbox.setPaintProperty(
        id,
        'raster-opacity',
        opacity
      )
    }
  }, [opacity])

  React.useEffect(() => {
    if (!state.mapbox) return
    for (let i = 0; i < splitNum; i++) {
      const id = `${layerId}-${i}`
      if (!state.mapbox.getLayer(id)) continue
      state.mapbox.setLayoutProperty(
        id,
        'visibility',
        visibility && url ? 'visible' : 'none'
      )
    }
  }, [url, visibility])

  const load = async () => {
    if (!url || coordinates.length !== 4) {
      remove()
      return
    }
    const rows = await splitImageWithCoordinates(url, coordinates, splitNum)
    rows.forEach((row, i) => {
      const sid = `${sourceId}-${i}`
      const lid = `${layerId}-${i}`
      if (state.map.hasSource(sid)) {
        state.map.updateImageSource(sid, row.url, row.coordinates)
      } else {
        state.map.addSource(sid, {
          'type': 'image',
          'url': row.url,
          coordinates: row.coordinates
        })
        state.map.addLayer({
          id: lid,
          minzoom: 3,
          'type': 'raster',
          'source': sid,
          'paint': {
            'raster-resampling': 'nearest',
            'raster-opacity': opacity,
            'raster-fade-duration': 0,
          }
        })
      }
    })
  }

  const remove = () => {
    for (let i = 0; i < splitNum; i++) {
      state.map.removeLayer(`${layerId}-${i}`)
      state.map.removeSource(`${sourceId}-${i}`)
    }
  }

  React.useEffect(() => {
    state.mapbox?.on('style.load', load)
    return () => {
      state.mapbox?.off('style.load', load)
    }
  }, [load])

  return <></>
}
